// modules/calendar/api/ExternAppointmentScheduleApi.ts
import { Container, IInit } from '../../../common/container/Container'
import { ExternAppointmentSchedule } from '../models/ExternAppointmentSchedule'
import { CalendarContainerConfig } from '../container'
import { Observable, of, throwError } from 'rxjs'
import { Query } from '../../../common/api/Query'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { catchError, map } from 'rxjs/operators'
import { ExternAppointmentScheduleDTO, toModel } from '../models/ExternAppointmentScheduleDTO'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { prepareURL } from '../../../common/api/http-helpers'
import { STATUS_SERVICE_KEY } from '../../../container/app'

export interface IExternAppointmentScheduleApi extends IInit {
  getFilteredList(
    q: Query<ExternAppointmentSchedule>
  ): Observable<ItemList<ExternAppointmentSchedule>>
  getById(id: string): Observable<ExternAppointmentSchedule | undefined>
  add(schedule: ExternAppointmentScheduleDTO): Observable<ExternAppointmentSchedule | undefined>
  update(
    id: string,
    schedule: ExternAppointmentScheduleDTO
  ): Observable<ExternAppointmentSchedule | undefined>
  delete(id: string): Observable<boolean>
  getProfessionalAvailability(
    userId: string,
    startDays?: number,
    days?: number
  ): Observable<ItemList<ExternAppointmentSchedule>>
}

export class ExternAppointmentScheduleApi implements IExternAppointmentScheduleApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url =
      (this._container.config as CalendarContainerConfig).moduleFullUrl + '/extern-schedules'
  }

  getFilteredList(
    q: Query<ExternAppointmentSchedule>
  ): Observable<ItemList<ExternAppointmentSchedule>> {
    return this._httpClient
      .get<ItemList<ExternAppointmentSchedule>>({ url: prepareURL(`${this._url}/list`, q) })
      .pipe(
        map<ItemList<ExternAppointmentScheduleDTO>, ItemList<ExternAppointmentSchedule>>((dto) => {
          const itemList = emptyList<ExternAppointmentSchedule>()
          itemList.count = dto.count
          itemList.items = dto.items.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<ExternAppointmentSchedule>())
        })
      )
  }

  getById(id: string): Observable<ExternAppointmentSchedule | undefined> {
    return this._httpClient.get<ExternAppointmentScheduleDTO>({ url: `${this._url}/${id}` }).pipe(
      map<ExternAppointmentScheduleDTO, ExternAppointmentSchedule>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  add(schedule: ExternAppointmentScheduleDTO): Observable<ExternAppointmentSchedule | undefined> {
    return this._httpClient
      .post<ExternAppointmentScheduleDTO>({ url: this._url, body: schedule })
      .pipe(
        map<ExternAppointmentScheduleDTO, ExternAppointmentSchedule>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return throwError(undefined)
        })
      )
  }

  update(
    id: string,
    schedule: ExternAppointmentScheduleDTO
  ): Observable<ExternAppointmentSchedule | undefined> {
    return this._httpClient
      .put<ExternAppointmentScheduleDTO>({ url: `${this._url}/${id}`, body: schedule })
      .pipe(
        map<ExternAppointmentScheduleDTO, ExternAppointmentSchedule>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      map(() => {
        this._statusService.sendStatus({ variant: 'success' })
        return true
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getProfessionalAvailability(
    userId: string,
    startDays: number = 2,
    days: number = 15
  ): Observable<ItemList<ExternAppointmentSchedule>> {
    return this._httpClient
      .get<{
        items: ExternAppointmentScheduleDTO[]
        professionalId: string
        startDate: string
        endDate: string
      }>({
        url: prepareURL(
          `${this._url.replace('/extern-schedules', '')}/professionals/${userId}/availability`,
          {
            startDays: startDays.toString(),
            days: days.toString(),
          }
        ),
      })
      .pipe(
        map((response) => {
          const itemList = emptyList<ExternAppointmentSchedule>()

          // Filtrar para asegurarnos de que solo se incluyan los horarios del profesional solicitado
          const filteredItems = response.items.filter(
            (item) => item.userId === userId || item.userId === response.professionalId
          )

          itemList.count = filteredItems.length
          itemList.items = filteredItems.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<ExternAppointmentSchedule>())
        })
      )
  }
}
